import React, { useMemo } from 'react';
import { Alert, Badge, ButtonGroup, Card, Col, Form, Row, ToggleButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  BILLING_PERIODS,
  getPlanQuote,
  getPromoBillingPeriodLabelKey,
  isPlanBillingPeriodEnabled
} from '../salesPricing';

const badgeVariantByPeriod = {
  any: 'secondary',
  monthly: 'info',
  yearly: 'dark'
};

const formatMoney = (amount, currency, locale) => {
  const n = Number(amount || 0);
  return new Intl.NumberFormat(locale || undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number.isNaN(n) ? 0 : n);
};

const DEFAULT_COUNTRY_DISPLAY = { show_usd: true, show_local: false };
const COUNTRY_LOCAL_CURRENCY = {
  AR: 'ARS',
  BR: 'BRL',
  CL: 'CLP',
  CN: 'CNY',
  US: 'USD'
};

const normalizeCountryCode = value => String(value || '').trim().toUpperCase();

const getLanguageFallbackCountry = language => {
  const lang = String(language || '').toLowerCase();
  if (lang.startsWith('es')) return 'AR';
  if (lang.startsWith('pt')) return 'BR';
  if (lang.startsWith('zh')) return 'CN';
  return 'US';
};

const getDisplayConfigForCountry = (plan, countryCode) => {
  const normalizedCountry = normalizeCountryCode(countryCode);
  const pricingDisplay = plan?.pricing_display || {};
  return pricingDisplay[normalizedCountry] || DEFAULT_COUNTRY_DISPLAY;
};

const getCountryPricingContext = (plan, countryCode) => {
  const normalizedCountry = normalizeCountryCode(countryCode) || 'US';
  const localCurrency = COUNTRY_LOCAL_CURRENCY[normalizedCountry] || 'USD';
  const displayConfig = getDisplayConfigForCountry(plan, normalizedCountry);
  const showUsdRaw = displayConfig.show_usd !== false;
  const showLocal = displayConfig.show_local === true && localCurrency !== 'USD';
  const showUsd = showUsdRaw || !showLocal;
  return { normalizedCountry, localCurrency, showUsd, showLocal };
};

const readPlanAmount = (plan, billingPeriod, currencyCode, type = 'monthly') => {
  const code = String(currencyCode || 'USD').toLowerCase();
  if (type === 'yearlyTotal') {
    return Number(plan?.pricing?.yearly?.[`${code}_total`]?.amount || 0);
  }
  if (billingPeriod === BILLING_PERIODS.YEARLY) {
    return Number(plan?.pricing?.yearly?.[`${code}_per_month`]?.amount || 0);
  }
  return Number(plan?.pricing?.monthly?.[code]?.amount || 0);
};

const getPlanQuoteForCurrency = (plan, billingPeriod, promo, currencyCode) => {
  if (!plan?.pricing) return null;

  const normalizedBillingPeriod = billingPeriod === BILLING_PERIODS.YEARLY && isPlanBillingPeriodEnabled(plan, BILLING_PERIODS.YEARLY)
    ? BILLING_PERIODS.YEARLY
    : BILLING_PERIODS.MONTHLY;

  const code = String(currencyCode || 'USD').toLowerCase();
  const monthlyPricing = plan.pricing.monthly || {};
  const yearlyPricing = plan.pricing.yearly || {};

  const basePerMonth = normalizedBillingPeriod === BILLING_PERIODS.YEARLY
    ? yearlyPricing[`${code}_per_month`]
    : monthlyPricing[code];
  const baseTotal = normalizedBillingPeriod === BILLING_PERIODS.YEARLY
    ? yearlyPricing[`${code}_total`]
    : monthlyPricing[code];

  const baseAmount = Number(baseTotal?.amount || 0);
  let discountAmount = 0;

  const promoRequiredPeriod = promo?.applicable_billing_period || 'any';
  const isCompatible = promoRequiredPeriod === 'any' || promoRequiredPeriod === normalizedBillingPeriod;

  if (promo && isCompatible) {
    const discountValue = Number(promo.discount_value || 0);
    if (promo.discount_type === 'percentage') {
      discountAmount = baseAmount * (discountValue / 100);
    } else if (promo.discount_type === 'fixed_amount') {
      discountAmount = discountValue;
    } else if (promo.discount_type === 'free_months') {
      discountAmount = Number(basePerMonth?.amount || 0) * discountValue;
    }
  }

  if (discountAmount > baseAmount) {
    discountAmount = baseAmount;
  }

  return {
    billingPeriod: normalizedBillingPeriod,
    isCompatible,
    baseAmount,
    discountAmount,
    finalAmount: Math.max(baseAmount - discountAmount, 0),
    basePerMonth,
  };
};

const SalesPlanSelector = ({ plans, selectedPlanId, onSelectPlanId, billingPeriod, onBillingPeriodChange, promo, viewerCountry }) => {
  const { t, i18n } = useTranslation();
  const countryCode = useMemo(
    () => normalizeCountryCode(viewerCountry) || getLanguageFallbackCountry(i18n.language),
    [viewerCountry, i18n.language]
  );

  const selectedPlan = useMemo(
    () => plans.find(item => String(item.id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId]
  );
  const selectedDisplayContext = useMemo(
    () => getCountryPricingContext(selectedPlan, countryCode),
    [selectedPlan, countryCode]
  );
  const selectedUsdQuote = useMemo(
    () => getPlanQuote(selectedPlan, billingPeriod, promo),
    [billingPeriod, promo, selectedPlan]
  );
  const selectedLocalQuote = useMemo(
    () => getPlanQuoteForCurrency(selectedPlan, billingPeriod, promo, selectedDisplayContext.localCurrency),
    [billingPeriod, promo, selectedPlan, selectedDisplayContext.localCurrency]
  );
  const selectedQuote = useMemo(() => {
    const useLocal = selectedDisplayContext.showLocal && Number(selectedLocalQuote?.baseAmount || 0) > 0;
    if (useLocal) {
      return {
        ...selectedLocalQuote,
        currencyCode: selectedDisplayContext.localCurrency,
      };
    }
    return {
      ...selectedUsdQuote,
      currencyCode: 'USD',
    };
  }, [selectedDisplayContext.localCurrency, selectedDisplayContext.showLocal, selectedLocalQuote, selectedUsdQuote]);
  const showSelectedUsdSecondary = selectedDisplayContext.showUsd && selectedQuote?.currencyCode !== 'USD' && Number(selectedUsdQuote?.baseAmount || 0) > 0;

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>{t('SALES.BILLING_PERIOD')}</Form.Label>
        <ButtonGroup className="w-100">
          <ToggleButton
            id="billing-monthly"
            type="radio"
            variant={billingPeriod === BILLING_PERIODS.MONTHLY ? 'primary' : 'outline-primary'}
            name="billing-period"
            value={BILLING_PERIODS.MONTHLY}
            checked={billingPeriod === BILLING_PERIODS.MONTHLY}
            onChange={event => onBillingPeriodChange(event.currentTarget.value)}
          >
            {t('SALES.BILLING_PERIOD_MONTHLY')}
          </ToggleButton>
          <ToggleButton
            id="billing-yearly"
            type="radio"
            variant={billingPeriod === BILLING_PERIODS.YEARLY ? 'primary' : 'outline-primary'}
            name="billing-period"
            value={BILLING_PERIODS.YEARLY}
            checked={billingPeriod === BILLING_PERIODS.YEARLY}
            onChange={event => onBillingPeriodChange(event.currentTarget.value)}
          >
            {t('SALES.BILLING_PERIOD_YEARLY')}
          </ToggleButton>
        </ButtonGroup>
      </Form.Group>

      <div className="mb-2 fw-semi-bold">{t('SALES.PLAN_PREVIEW')}</div>
      <Row className="g-3 mb-3">
        {plans.map(plan => {
          const isSelected = String(plan.id) === String(selectedPlanId);
          const yearlyEnabled = isPlanBillingPeriodEnabled(plan, BILLING_PERIODS.YEARLY);
          const displayContext = getCountryPricingContext(plan, countryCode);
          const monthlyUsd = plan?.pricing?.monthly?.usd?.amount;
          const yearlyUsdPerMonth = plan?.pricing?.yearly?.usd_per_month?.amount;
          const yearlyUsdTotal = plan?.pricing?.yearly?.usd_total?.amount;
          const monthlyLocal = readPlanAmount(plan, BILLING_PERIODS.MONTHLY, displayContext.localCurrency);
          const yearlyLocalPerMonth = readPlanAmount(plan, BILLING_PERIODS.YEARLY, displayContext.localCurrency);
          const yearlyLocalTotal = readPlanAmount(plan, BILLING_PERIODS.YEARLY, displayContext.localCurrency, 'yearlyTotal');
          const monthlyPrimaryCurrency = displayContext.showLocal && monthlyLocal > 0
            ? displayContext.localCurrency
            : 'USD';
          const monthlyPrimaryAmount = monthlyPrimaryCurrency === 'USD' ? Number(monthlyUsd || 0) : monthlyLocal;
          const showDual = displayContext.showUsd && displayContext.showLocal && Number(monthlyUsd || 0) > 0 && monthlyLocal > 0;
          return (
            <Col md={6} key={plan.id}>
              <Card
                role="button"
                onClick={() => onSelectPlanId(String(plan.id))}
                className={isSelected ? 'border-primary shadow-sm h-100' : 'h-100'}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <div className="fw-semi-bold">{plan.name}</div>
                      <div className="text-600 fs--1">{plan.description || t('SALES.NO_DESCRIPTION')}</div>
                    </div>
                    {promo ? (
                      <Badge bg={badgeVariantByPeriod[promo.applicable_billing_period || 'any'] || 'secondary'}>
                        {t(getPromoBillingPeriodLabelKey(promo.applicable_billing_period))}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mb-2">
                    <div className="text-600 fs--2">{t('SALES.MONTHLY_PRICE')}</div>
                    <div className="fw-semi-bold">{formatMoney(monthlyPrimaryAmount, monthlyPrimaryCurrency, i18n.language)} {monthlyPrimaryCurrency}</div>
                    {showDual && (
                      <div className="text-600 fs--2">
                        {formatMoney(Number(monthlyUsd || 0), 'USD', i18n.language)} USD
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <div className="text-600 fs--2">{t('SALES.YEARLY_PRICE')}</div>
                    <div className="fw-semi-bold">
                      {yearlyEnabled
                        ? (displayContext.showLocal && yearlyLocalPerMonth > 0
                          ? `${formatMoney(yearlyLocalPerMonth, displayContext.localCurrency, i18n.language)} ${displayContext.localCurrency} / ${t('SALES.PER_MONTH')} · ${formatMoney(yearlyLocalTotal, displayContext.localCurrency, i18n.language)} ${displayContext.localCurrency} / ${t('SALES.PER_YEAR')}`
                          : `${formatMoney(yearlyUsdPerMonth, 'USD', i18n.language)} USD / ${t('SALES.PER_MONTH')} · ${formatMoney(yearlyUsdTotal, 'USD', i18n.language)} USD / ${t('SALES.PER_YEAR')}`)
                        : t('SALES.NOT_AVAILABLE')}
                    </div>
                    {showDual && yearlyEnabled && Number(yearlyUsdPerMonth || 0) > 0 && (
                      <div className="text-600 fs--2">
                        {formatMoney(yearlyUsdPerMonth, 'USD', i18n.language)} USD / {t('SALES.PER_MONTH')} · {formatMoney(yearlyUsdTotal, 'USD', i18n.language)} USD / {t('SALES.PER_YEAR')}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {(plan.preview_features || []).slice(0, 6).map(feature => (
                      <Badge bg="soft-primary" text="primary" key={`${plan.id}-${feature.key}`} className="px-2 py-1">
                        {feature.label}: {String(feature.value)}
                      </Badge>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {!selectedPlan ? (
        <Alert variant="secondary" className="mb-0">{t('SALES.SELECT_PLAN_TO_PREVIEW')}</Alert>
      ) : (
        <Card className="bg-light border-0">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <div className="fw-semi-bold">{t('SALES.FINAL_PRICE')}</div>
              {promo ? (
                <Badge bg={badgeVariantByPeriod[promo.applicable_billing_period || 'any'] || 'secondary'}>
                  {promo.campaign_name} · {t(getPromoBillingPeriodLabelKey(promo.applicable_billing_period))}
                </Badge>
              ) : null}
            </div>

            {selectedQuote && !selectedQuote.isCompatible ? (
              <Alert variant="warning" className="mb-3">
                {t('SALES.PROMO_REQUIRES_BILLING_PERIOD', {
                  billingPeriod: t(getPromoBillingPeriodLabelKey(promo?.applicable_billing_period))
                })}
              </Alert>
            ) : null}

            <Row className="g-2">
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.BASE_PRICE')}</div>
                <div className="fw-semi-bold">{formatMoney(selectedQuote?.baseAmount, selectedQuote?.currencyCode, i18n.language)} {selectedQuote?.currencyCode}</div>
                {showSelectedUsdSecondary && (
                  <div className="text-600 fs--2">{formatMoney(selectedUsdQuote?.baseAmount, 'USD', i18n.language)} USD</div>
                )}
              </Col>
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.PROMO_DISCOUNT')}</div>
                <div className="fw-semi-bold text-success">-{formatMoney(selectedQuote?.discountAmount, selectedQuote?.currencyCode, i18n.language)} {selectedQuote?.currencyCode}</div>
                {showSelectedUsdSecondary && (
                  <div className="text-600 fs--2 text-success">-{formatMoney(selectedUsdQuote?.discountAmount, 'USD', i18n.language)} USD</div>
                )}
              </Col>
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.FINAL_PRICE')}</div>
                <div className="fw-bold text-primary">{formatMoney(selectedQuote?.finalAmount, selectedQuote?.currencyCode, i18n.language)} {selectedQuote?.currencyCode}</div>
                {showSelectedUsdSecondary && (
                  <div className="text-600 fs--2">{formatMoney(selectedUsdQuote?.finalAmount, 'USD', i18n.language)} USD</div>
                )}
              </Col>
              <Col sm={6}>
                <div className="text-600 fs--2">{t('SALES.BILLING_PERIOD')}</div>
                <div className="fw-semi-bold">{t(getPromoBillingPeriodLabelKey(selectedQuote?.billingPeriod))}</div>
              </Col>
              <Col sm={6}>
                <div className="text-600 fs--2">{t('SALES.PER_MONTH')}</div>
                <div className="fw-semi-bold">{formatMoney(selectedQuote?.basePerMonth?.amount, selectedQuote?.currencyCode, i18n.language)} {selectedQuote?.currencyCode}</div>
                {showSelectedUsdSecondary && Number(selectedUsdQuote?.basePerMonth?.amount || 0) > 0 && (
                  <div className="text-600 fs--2">{formatMoney(selectedUsdQuote.basePerMonth.amount, 'USD', i18n.language)} USD</div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default SalesPlanSelector;